// Story 7.6 — curated external news card (hltv_rss / lolesports_json).
// Epic 8 — rendered on parchment (ParchmentPost).
//
// The SOURCE claims the top-left author slot — these are the source's
// words, not the creator's. If the item is ABOUT a linked creator, their
// mark appears in the top-right subject slot, and the printed footer
// preposition flips to "About @handle · via <source>". Primary CTA is the
// honest link-out ("Read on HLTV.org →", new tab) — no reader mode, no
// inline article body, and the card surface itself is NOT clickable (AC 8).

import { ParchmentPost } from "@greenhold/components";

import {
  ProfileLink,
  parchmentCtaClass,
  relativeTime,
  sourceDisplayName,
  wearSeed,
} from "./feedCardShared";
import type { FeedCard } from "./useFeed";

export function CuratedNewsCard({ card }: { card: FeedCard }) {
  const source = sourceDisplayName(card.source_kind);
  const linkedCreator = card.creator_user_id !== null;
  const creatorLabel = card.creator_handle
    ? `@${card.creator_handle}`
    : (card.creator_display_name ?? "a creator");

  // ParchmentPost's footer carries a single preposition; the closed
  // "About … · via …" vocabulary is composed into provenanceName so the
  // honesty seal reads exactly as before.
  const provenanceName = linkedCreator
    ? `${creatorLabel} · via ${source}`
    : source;

  return (
    <ParchmentPost
      author={source}
      subject={
        linkedCreator && card.creator_handle ? (
          <ProfileLink handle={card.creator_handle} label={creatorLabel} />
        ) : undefined
      }
      provenance={linkedCreator ? "about" : "via"}
      provenanceName={provenanceName}
      time={relativeTime(card.published_at)}
      title={card.title}
      pinned={card.is_pinned}
      seed={wearSeed(card.id)}
    >
      {card.body ? (
        <p className="m-0 whitespace-pre-line">{card.body}</p>
      ) : null}
      {card.external_url ? (
        <p className="m-0 mt-3">
          <a
            href={card.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className={parchmentCtaClass}
            data-testid="card-link-out"
          >
            Read on {source} →
          </a>
        </p>
      ) : null}
    </ParchmentPost>
  );
}
